import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SequenceSchemaType, TagSchemaSupportedFolderItemType} from 'constants/RegistryConstants';
import {ExtraActionButtons} from 'metadata/FieldActionButtons';
import {getFieldOptions} from 'registry/field/FieldTableOptionGetters';
import FieldTableRow from 'registry/field/FieldTableRow';
import {StorableSchema} from 'samples/storables/StorableSchemaConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaInterface} from 'schemas/SchemaConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';

import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';
import SchemaInterfaceFieldAdvancedSettingsIcon from './SchemaInterfaceFieldAdvancedSettingsIcon';

export interface SchemaInterfaceFieldTableRowProps {
  schemaFieldSelectors: Array<SchemaFieldSelector>;
  ownerUrlId: string;
  id: string;
  initialField: SchemaField;
  onCancel: (id: string) => void;
  onEdit: (id: string) => void;
  requestChange: (id: string, fieldWithChanges: SchemaField) => void;
  requestRemove: (id: string) => void;
  tagSchemas?: Array<TagSchemaSummaryGraphQL>;
  storableSchemas?: Array<StorableSchema>;
  schemaInterfaces?: Array<SchemaInterface>;
  shouldShowIsRequired: boolean;
  submissionWarnings: Array<JSX.Element>;
  fileType?: TagSchemaSupportedFolderItemType | null;
  sequenceType?: SequenceSchemaType | null;
  isSchemaOrInterfaceBeingCreated: boolean;
}

export default function SchemaInterfaceFieldTableRow(props: SchemaInterfaceFieldTableRowProps): JSX.Element {
  const includeSequenceLinks =
    props.fileType === ModelTypeConstants.SEQUENCE &&
    (props.sequenceType === SequenceSchemaType.DNA_SEQUENCE ||
      props.sequenceType === SequenceSchemaType.RNA_SEQUENCE);

  const includeProteinLinks = props.fileType === ModelTypeConstants.PROTEIN;

  const extraActionButtons: ExtraActionButtons = {viewMode: [], editMode: []};
  if (!props.isSchemaOrInterfaceBeingCreated) {
    extraActionButtons.viewMode.push(
      <SchemaInterfaceFieldAdvancedSettingsIcon
        field={props.initialField}
        onUpdateField={(update) => {
          // Convert onUpdateField format to requestChange format
          const updatedField = {...props.initialField, ...update};
          props.requestChange(props.id, updatedField);
        }}
        key="advanced-settings"
      />
    );
  }

  return (
    <FieldTableRow
      availableFieldOptions={getFieldOptions({
        initialField: props.initialField,
        schemaFieldSelectors: props.schemaFieldSelectors,
        storableSchemas: props.storableSchemas ?? [],
        tagSchemas: props.tagSchemas ?? [],
        schemaInterfaces: props.schemaInterfaces ?? [],
        includeBatchLinks: true,
        includeBooleanField: true,
        includeGenericBioentityLinks: false,
        includeSnapshotField: false,
        includeJSONField: false,
        includeArchivedSchemas: true,
        includeSequenceLinks,
        includeDnaSequenceSpecificLinks: false,
        includeProteinLinks,
        sequenceSchemaType: props.sequenceType,
      })}
      {...props}
      isUneditable={false}
      isRemoveOnly={true}
      disableDrag={true}
      shouldShowWarehouseWarning={true}
      schemaId={null}
      advancedSettingsComponentForFieldCreation={SchemaInterfaceFieldAdvancedSettingsIcon}
      extraActionButtons={extraActionButtons}
    />
  );
}
